import { readCareCommunicationPriority } from '@/lib/medcom/careCommunicationPriority';
import { sksAdministrativeRoleDisplay } from '@/lib/medcom/sksAdministrativeRole';

/** Official Danish CPR. Only this system fills the CPR field. */
export const OFFICIAL_CPR_SYSTEM = 'urn:oid:1.2.208.176.1.2';
/** X-eCPR (erstatnings-CPR). */
export const X_ECPR_SYSTEM = 'urn:oid:1.2.208.176.1.6.1.1';
/**
 * D-eCPR used when building fixtures. The parser also accepts
 * urn:oid:1.2.208.176.1.6.1.2 and any system starting with this OID.
 */
export const D_ECPR_SYSTEM = 'urn:oid:1.2.208.176.1.6.1.3';
const D_ECPR_SYSTEM_ALT = 'urn:oid:1.2.208.176.1.6.1.2';

export function isOfficialCprSystem(system: string | null | undefined): boolean {
  return system === OFFICIAL_CPR_SYSTEM;
}

export function isXEcprSystem(system: string | null | undefined): boolean {
  return system === X_ECPR_SYSTEM;
}

export function isDEcprSystem(system: string | null | undefined): boolean {
  return system === D_ECPR_SYSTEM_ALT || Boolean(system?.startsWith(D_ECPR_SYSTEM));
}

export interface BundleTextSegment {
  /** The payload's identifier in the letter, used to pick it when forwarding. */
  segmentId: string | null;
  /** 1-based index in Communication.payload. */
  payloadIndex: number;
  text: string;
  timestamp: string | null;
  signature: BundleSignature | null;
}

export interface BundleAttachment {
  /** The payload's identifier in the letter, used to pick it when forwarding. */
  segmentId: string | null;
  /** 1-based index in Communication.payload. */
  payloadIndex: number;
  /**
   * 1-based text-segment number of the nearest preceding text payload.
   * Null when the attachment comes before any text. FHIR has no parent link.
   */
  textSegmentNumber: number | null;
  title: string | null;
  contentType: string | null;
  data: string | null;
  url: string | null;
  creation: string | null;
  /** Payload datetime extension. Separate from contentAttachment.creation. */
  authoredAt: string | null;
  authorName: string | null;
  authorPhone: string | null;
}

/** Author of one text payload: practitioner, role, contact, time, and organisation. */
export interface BundleSignature {
  name: string | null;
  role: string | null;
  phone: string | null;
  at: string | null;
  organization: string | null;
}

export interface BundleOrganization {
  id: string;
  name: string | null;
  sor: string | null;
}

export interface ParsedBundleContent {
  patientName: string | null;
  /** Patient.name.given from the letter, in order. */
  patientGiven: string[];
  /** Patient.name.family from the letter. */
  patientFamily: string | null;
  cpr: string | null;
  xEcpr: string | null;
  dEcpr: string | null;
  categoryCode: string | null;
  topic: string | null;
  priority: string | null;
  activityCode: string | null;
  messageTimestamp: string | null;
  textSegments: BundleTextSegment[];
  attachments: BundleAttachment[];
  senderOrganization: string | null;
  senderSor: string | null;
  /** GLN on the organisation that sent the letter. */
  senderGln: string | null;
  receiverOrganization: string | null;
  receiverSor: string | null;
  /** GLN on the organisation the letter was sent to. */
  receiverGln: string | null;
  senderPractitionerName: string | null;
  senderRole: string | null;
  senderPhone: string | null;
  senderEmail: string | null;
  /** CareTeam or other party on medcom-core-sender-extension. */
  specificSenderName: string | null;
  /** Resource type on the sender extension: CareTeam → 'team'. */
  specificSenderKind: 'person' | 'team' | 'organization' | null;
  /** Person on Communication.recipient when that is not the organisation. */
  specificRecipientName: string | null;
  specificRecipientRole: string | null;
  /** Person or care team. An organisation recipient has neither. */
  specificRecipientKind: 'person' | 'team' | null;
  /** Signature of the text written for this message, not an earlier copied segment. */
  signature: BundleSignature | null;
  /** First episode identifier, or the encounter id when the letter has none. */
  episodeOfCareId: string | null;
  episodeOfCareIdentifiers: BundleEpisodeOfCareIdentifier[];
  /** Encounter.status from the letter, when the letter has an Encounter. */
  encounterStatus: string | null;
  encounterClassCode: string | null;
  encounterClassSystem: string | null;
  otherIdentifiers: { system: string; value: string }[];
  /** Organization resources on this letter. Used to resolve a segment’s org from this bundle only. */
  organizations: BundleOrganization[];
}

/** One episodeOfCare identifier, with the register it comes from when that is known. */
export interface BundleEpisodeOfCareIdentifier {
  /** Shown on the letter. A `urn:uuid:` prefix is left off. */
  value: string;
  source: string | null;
  /** Identifier.system from the letter. */
  system: string | null;
  /** Identifier.value from the letter, including a `urn:uuid:` prefix. */
  rawValue: string;
}

/** Encounter fields a reply or forward sends on to Communicate. */
export type ReplyEncounterPayload = {
  episodeOfCare: {
    identifiers: { system?: string; value: string }[];
  };
  encounter: {
    status?: string;
    class?: { system?: string; code: string };
    episodeOfCare: { identifier: { system?: string; value: string } }[];
  };
};

type Resource = Record<string, unknown>;

type HumanName = { given?: unknown; family?: string; text?: string };

type Extension = {
  url?: string;
  valueDateTime?: string;
  valueReference?: { reference?: string };
  valueContactPoint?: { system?: string; value?: string };
  valueIdentifier?: { value?: string; assigner?: { reference?: string } };
};

type RawPayload = {
  extension?: Extension[];
  contentString?: string;
  contentAttachment?: {
    title?: string;
    contentType?: string;
    data?: string;
    url?: string;
    creation?: string;
  };
};

type Telecom = { system?: string; value?: string };

function refId(ref: unknown): string | null {
  const reference = referenceString(ref);
  if (!reference) return null;
  return reference.split('/').pop() ?? null;
}

function referenceString(ref: unknown): string | null {
  if (typeof ref === 'string' && ref.trim()) return ref.trim();
  if (ref && typeof ref === 'object') {
    const reference = (ref as { reference?: unknown }).reference;
    if (typeof reference === 'string' && reference.trim()) return reference.trim();
  }
  return null;
}

function stringValue(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  return text || null;
}

function personName(resource: Resource | undefined): string | null {
  if (!resource) return null;
  const nameObj = (resource.name as HumanName[] | undefined)?.[0];
  if (!nameObj) return null;
  return humanNameDisplay(nameObj);
}

function givenNames(value: unknown): string[] {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }
  if (!Array.isArray(value)) return [];
  return value
    .filter((part): part is string => typeof part === 'string')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

function humanNameDisplay(nameObj: HumanName): string | null {
  const text = nameObj.text?.trim();
  if (text) return text;
  const joined = [givenNames(nameObj.given).join(' '), nameObj.family]
    .filter(Boolean)
    .join(' ')
    .trim();
  return joined || null;
}

function roleLabel(role: Resource | undefined): string | null {
  if (!role) return null;
  const code = (
    role.code as { coding?: { code?: string; display?: string }[]; text?: string }[] | undefined
  )?.[0];
  if (!code) return null;
  const text = code.text?.trim();
  if (text) return text;
  const coding = code.coding?.[0];
  const display = coding?.display?.trim();
  if (display) return display;
  return sksAdministrativeRoleDisplay(coding?.code) ?? stringValue(coding?.code);
}

function extensionByUrl(
  extensions: Extension[] | undefined,
  fragment: string,
): Extension | undefined {
  return extensions?.find((extension) => extension.url?.includes(fragment));
}

function contactValue(
  extensions: Extension[] | undefined,
  system: 'phone' | 'email',
): string | null {
  const point = extensionByUrl(extensions, 'contact-extension')?.valueContactPoint;
  if (!point || point.system !== system) return null;
  return stringValue(point.value);
}

function resourceMap(resources: Resource[]): Map<string, Resource> {
  const byRef = new Map<string, Resource>();
  for (const resource of resources) {
    const type = resource.resourceType;
    const id = resource.id;
    if (typeof type === 'string' && typeof id === 'string') {
      byRef.set(`${type}/${id}`, resource);
      byRef.set(id, resource);
    }
  }
  return byRef;
}

function resourceAt(byRef: Map<string, Resource>, ref: unknown): Resource | undefined {
  const reference = referenceString(ref);
  if (!reference) return undefined;
  return byRef.get(reference) ?? byRef.get(reference.split('/').pop() ?? '');
}

function practitionerOf(
  role: Resource | undefined,
  byRef: Map<string, Resource>,
): Resource | undefined {
  if (!role) return undefined;
  return resourceAt(byRef, role.practitioner);
}

interface PayloadAuthor {
  name: string | null;
  role: string | null;
  roleResource: Resource | undefined;
  practitioner: Resource | undefined;
  phone: string | null;
  at: string | null;
  organization: string | null;
}

function roleOrganizationName(
  role: Resource | undefined,
  byRef: Map<string, Resource>,
): string | null {
  if (!role) return null;
  return organizationLabel(resourceAt(byRef, role.organization));
}

/** Name when the organisation has one, otherwise its SOR code. Never a different organisation. */
function organizationLabel(organization: Resource | undefined): string | null {
  const name = stringValue(organization?.name);
  if (name) return name;
  const sor = sorValue(organization);
  return sor ? `SOR ${sor}` : null;
}

function payloadIdentifier(payload: RawPayload): string | null {
  return stringValue(
    extensionByUrl(payload.extension, 'identifier-extension')?.valueIdentifier?.value,
  );
}

/**
 * MedCom R3 3.1.3.4: a payload's sender organisation is Provenance.agent.who
 * for the Provenance whose source entity identifier matches the payload.
 */
function provenanceOrganizationByPayloadId(
  resources: Resource[],
  byRef: Map<string, Resource>,
): Map<string, string | null> {
  const byPayloadId = new Map<string, string | null>();
  for (const resource of resources) {
    if (resource.resourceType !== 'Provenance') continue;
    const agents = (resource.agent as { who?: unknown }[] | undefined) ?? [];
    const organization = agents
      .map((agent) => organizationFromAgent(resourceAt(byRef, agent.who), byRef))
      .find((who) => who);
    if (!organization) continue;
    const label = organizationLabel(organization);
    const entities =
      (resource.entity as
        | { role?: string; what?: { identifier?: { value?: string } } }[]
        | undefined) ?? [];
    for (const entity of entities) {
      if (entity.role !== 'source') continue;
      const value = stringValue(entity.what?.identifier?.value);
      if (!value || byPayloadId.has(value)) continue;
      byPayloadId.set(value, label);
    }
  }
  return byPayloadId;
}

function payloadAuthor(
  payload: RawPayload,
  byRef: Map<string, Resource>,
  provenanceOrganization: Map<string, string | null>,
): PayloadAuthor {
  const extensions = payload.extension;
  const roleResource = resourceAt(
    byRef,
    extensionByUrl(extensions, 'practitioner-extension')?.valueReference,
  );
  const practitioner = practitionerOf(roleResource, byRef);
  const payloadId = payloadIdentifier(payload);
  const fromProvenance = payloadId !== null ? provenanceOrganization.get(payloadId) : undefined;
  return {
    name: personName(practitioner),
    role: roleLabel(roleResource),
    roleResource,
    practitioner,
    phone: contactValue(extensions, 'phone'),
    at: stringValue(extensionByUrl(extensions, 'datetime-extension')?.valueDateTime),
    organization:
      fromProvenance ??
      roleOrganizationName(roleResource, byRef) ??
      organizationLabel(payloadAssignerOrganization(payload, byRef)),
  };
}

function organizationFromAgent(
  who: Resource | undefined,
  byRef: Map<string, Resource>,
): Resource | undefined {
  if (!who) return undefined;
  if (who.resourceType === 'Organization') return who;
  if (who.resourceType === 'PractitionerRole') {
    return resourceAt(byRef, who.organization);
  }
  return undefined;
}

function payloadAssignerOrganization(
  payload: RawPayload,
  byRef: Map<string, Resource>,
): Resource | undefined {
  return resourceAt(
    byRef,
    extensionByUrl(payload.extension, 'identifier-extension')?.valueIdentifier?.assigner,
  );
}

function signatureFrom(author: PayloadAuthor): BundleSignature | null {
  if (!author.name && !author.role && !author.phone && !author.at && !author.organization) {
    return null;
  }
  return {
    name: author.name,
    role: author.role,
    phone: author.phone,
    at: author.at,
    organization: author.organization,
  };
}

/** Person named by medcom-core-sender-extension. A CareTeam or organisation is not a contact. */
function messageSenderPerson(
  communication: Resource | undefined,
  byRef: Map<string, Resource>,
): PayloadAuthor | null {
  const extensions = communication?.extension as Extension[] | undefined;
  const sender = resourceAt(byRef, extensionByUrl(extensions, 'sender-extension')?.valueReference);
  if (!sender) return null;
  if (sender.resourceType === 'PractitionerRole') {
    const practitioner = practitionerOf(sender, byRef);
    return {
      name: personName(practitioner),
      role: roleLabel(sender),
      roleResource: sender,
      practitioner,
      phone: null,
      at: null,
      organization: roleOrganizationName(sender, byRef),
    };
  }
  if (sender.resourceType === 'Practitioner') {
    return {
      name: personName(sender),
      role: null,
      roleResource: undefined,
      practitioner: sender,
      phone: null,
      at: null,
      organization: null,
    };
  }
  return null;
}

function specificSenderName(
  communication: Resource | undefined,
  byRef: Map<string, Resource>,
): string | null {
  const extensions = communication?.extension as Extension[] | undefined;
  const sender = resourceAt(byRef, extensionByUrl(extensions, 'sender-extension')?.valueReference);
  if (!sender) return null;
  if (sender.resourceType === 'CareTeam' || sender.resourceType === 'Organization') {
    return stringValue(sender.name);
  }
  if (sender.resourceType === 'Practitioner') return personName(sender);
  if (sender.resourceType === 'PractitionerRole') return personName(practitionerOf(sender, byRef));
  return null;
}

function specificSenderKind(
  communication: Resource | undefined,
  byRef: Map<string, Resource>,
): 'person' | 'team' | 'organization' | null {
  const extensions = communication?.extension as Extension[] | undefined;
  const sender = resourceAt(byRef, extensionByUrl(extensions, 'sender-extension')?.valueReference);
  switch (sender?.resourceType) {
    case 'CareTeam':
      return 'team';
    case 'Organization':
      return 'organization';
    case 'Practitioner':
    case 'PractitionerRole':
      return 'person';
    default:
      return null;
  }
}

function specificRecipient(
  communication: Resource | undefined,
  byRef: Map<string, Resource>,
): { name: string | null; role: string | null; kind: 'person' | 'team' | null } {
  const recipient = resourceAt(byRef, (communication?.recipient as unknown[] | undefined)?.[0]);
  if (!recipient) return { name: null, role: null, kind: null };
  if (recipient.resourceType === 'PractitionerRole') {
    return {
      name: personName(practitionerOf(recipient, byRef)),
      role: roleLabel(recipient),
      kind: 'person',
    };
  }
  if (recipient.resourceType === 'Practitioner') {
    return { name: personName(recipient), role: null, kind: 'person' };
  }
  if (recipient.resourceType === 'CareTeam') {
    return { name: stringValue(recipient.name), role: null, kind: 'team' };
  }
  return { name: null, role: null, kind: null };
}

function telecomValue(
  resources: Array<Resource | undefined>,
  system: 'phone' | 'email',
): string | null {
  for (const resource of resources) {
    const telecoms = (resource?.telecom as Telecom[] | undefined) ?? [];
    const value = stringValue(telecoms.find((item) => item.system === system)?.value);
    if (value) return value;
  }
  return null;
}

function episodeOfCareSource(system: string | null): string | null {
  if (!system) return null;
  const normalized = system.toLowerCase();
  if (normalized.includes('landspatientregisteret')) return 'Landspatientregisteret';
  if (normalized.includes('sor2.sum.dsdn.dk') || normalized.includes('1.2.208.176.1.1')) {
    return 'SOR';
  }
  return null;
}

function episodeOfCareIdentifiersFrom(
  resource: Resource | undefined,
): BundleEpisodeOfCareIdentifier[] {
  const episodes = resource?.episodeOfCare as
    | { identifier?: { system?: string; value?: string } }[]
    | undefined;
  const identifiers: BundleEpisodeOfCareIdentifier[] = [];
  for (const episode of episodes ?? []) {
    const raw = stringValue(episode.identifier?.value);
    if (!raw) continue;
    const system = stringValue(episode.identifier?.system);
    identifiers.push({
      value: raw.replace(/^urn:uuid:/, ''),
      rawValue: raw,
      system,
      source: episodeOfCareSource(system),
    });
  }
  return identifiers;
}

function encounterClassCoding(encounter: Resource | undefined): {
  system: string | null;
  code: string | null;
} {
  const klass = encounter?.class;
  if (!klass || typeof klass !== 'object') return { system: null, code: null };
  const direct = klass as {
    system?: string;
    code?: string;
    coding?: { system?: string; code?: string }[];
  };
  if (direct.code || direct.system) {
    return { system: stringValue(direct.system), code: stringValue(direct.code) };
  }
  const coding = direct.coding?.[0];
  return { system: stringValue(coding?.system), code: stringValue(coding?.code) };
}

/**
 * The letter's episode identifiers, unchanged, for a reply or forward.
 * Communicate builds the Encounter. An empty result means the letter has none.
 */
export function replyEncounterPayload(
  letter:
    | Pick<
        ParsedBundleContent,
        | 'episodeOfCareIdentifiers'
        | 'encounterStatus'
        | 'encounterClassCode'
        | 'encounterClassSystem'
      >
    | null
    | undefined,
): ReplyEncounterPayload | null {
  const episodes = letter?.episodeOfCareIdentifiers ?? [];
  if (episodes.length === 0) return null;
  const identifiers = episodes.map((episode) => ({
    ...(episode.system ? { system: episode.system } : {}),
    value: episode.rawValue,
  }));
  const classCode = letter?.encounterClassCode ?? null;
  return {
    episodeOfCare: { identifiers },
    encounter: {
      ...(letter?.encounterStatus ? { status: letter.encounterStatus } : {}),
      ...(classCode
        ? {
            class: {
              ...(letter?.encounterClassSystem ? { system: letter.encounterClassSystem } : {}),
              code: classCode,
            },
          }
        : {}),
      episodeOfCare: identifiers.map((identifier) => ({ identifier })),
    },
  };
}

function mergeEpisodeIdentifiers(
  ...groups: BundleEpisodeOfCareIdentifier[][]
): BundleEpisodeOfCareIdentifier[] {
  const merged: BundleEpisodeOfCareIdentifier[] = [];
  for (const group of groups) {
    for (const identifier of group) {
      const alreadyListed = merged.some(
        (item) => item.rawValue === identifier.rawValue && item.system === identifier.system,
      );
      if (alreadyListed) continue;
      merged.push(identifier);
    }
  }
  return merged;
}

/** `Forløbs-ID`, or `Forløbs-ID · SOR` when the register is known. */
export function episodeOfCareLabel(source: string | null): string {
  return source ? `Forløbs-ID · ${source}` : 'Forløbs-ID';
}

function sorValue(organization: Resource | undefined): string | null {
  const identifiers = organization?.identifier as { system?: string; value?: string }[] | undefined;
  return (
    identifiers?.find(
      (identifier) =>
        identifier.system === 'urn:oid:1.2.208.176.1.1' ||
        identifier.system?.includes('1.2.208.176.1.1'),
    )?.value ?? null
  );
}

function glnValue(organization: Resource | undefined): string | null {
  const identifiers = organization?.identifier as { system?: string; value?: string }[] | undefined;
  return (
    stringValue(
      identifiers?.find(
        (identifier) =>
          identifier.system === 'https://www.gs1.org/gln' ||
          identifier.system?.includes('gs1.org/gln'),
      )?.value,
    ) ?? null
  );
}

/** `dd.MM.yyyy HH:mm` in Europe/Copenhagen. */
export function formatMedcomDateTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Copenhagen',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((item) => item.type === type)?.value ?? '';
  const hour = part('hour') === '24' ? '00' : part('hour');
  return `${part('day')}.${part('month')}.${part('year')} ${hour}:${part('minute')}`;
}

export function parseCareCommunicationBundle(bundle: Record<string, unknown>): ParsedBundleContent {
  const entries = (bundle.entry as { resource?: Resource }[] | undefined) ?? [];
  const resources = entries.map((entry) => entry.resource).filter(Boolean) as Resource[];
  const byRef = resourceMap(resources);

  const patient = resources.find((resource) => resource.resourceType === 'Patient');
  const communication = resources.find((resource) => resource.resourceType === 'Communication');
  const provenance = resources.find((resource) => resource.resourceType === 'Provenance');
  const messageHeader = resources.find((resource) => resource.resourceType === 'MessageHeader');

  const nameObj = (patient?.name as HumanName[] | undefined)?.[0];
  const patientGiven = nameObj ? givenNames(nameObj.given) : [];
  const patientFamily = nameObj ? stringValue(nameObj.family) : null;
  const patientName = nameObj ? humanNameDisplay(nameObj) : null;
  const idents = patient?.identifier as { system?: string; value?: string }[] | undefined;
  const cprIdent = idents?.find((identifier) => isOfficialCprSystem(identifier.system));
  const xEcprIdent = idents?.find((identifier) => isXEcprSystem(identifier.system));
  const dEcprIdent = idents?.find((identifier) => isDEcprSystem(identifier.system));
  const knownSystems = new Set([
    OFFICIAL_CPR_SYSTEM,
    X_ECPR_SYSTEM,
    cprIdent?.system,
    xEcprIdent?.system,
    dEcprIdent?.system,
  ]);
  const otherIdents =
    idents?.filter((identifier) => identifier.system && !knownSystems.has(identifier.system)) ?? [];

  const categoryCodings = (
    communication?.category as { coding?: { code?: string }[] }[] | undefined
  )?.[0]?.coding;
  const categoryCode = categoryCodings?.[0]?.code ?? null;
  const topic = (communication?.topic as { text?: string } | undefined)?.text ?? null;
  const priority = readCareCommunicationPriority(communication?.priority);

  const encounterRef =
    (communication?.encounter as { reference?: string } | undefined)?.reference ?? null;
  const encounterResourceId = encounterRef ? (encounterRef.split('/').pop() ?? null) : null;
  const encounter = encounterResourceId
    ? byRef.get(encounterResourceId)
    : resources.find((resource) => resource.resourceType === 'Encounter');
  const episodeOfCareIdentifiers = mergeEpisodeIdentifiers(
    episodeOfCareIdentifiersFrom(encounter),
    episodeOfCareIdentifiersFrom(communication),
  );
  const episodeOfCareId = episodeOfCareIdentifiers[0]?.value ?? encounterResourceId;
  const encounterClass = encounterClassCoding(encounter);

  const rawPayloads = (communication?.payload as RawPayload[] | undefined) ?? [];
  const provenanceOrganization = provenanceOrganizationByPayloadId(resources, byRef);
  const textAuthors: PayloadAuthor[] = [];
  const textSegments: BundleTextSegment[] = [];
  const attachments: BundleAttachment[] = [];
  let textSegmentNumber = 0;
  rawPayloads.forEach((payload, index) => {
    const payloadIndex = index + 1;
    if (payload.contentString) {
      textSegmentNumber += 1;
      const author = payloadAuthor(payload, byRef, provenanceOrganization);
      textAuthors.push(author);
      textSegments.push({
        segmentId: payloadIdentifier(payload),
        payloadIndex,
        text: payload.contentString,
        timestamp: author.at,
        signature: signatureFrom(author),
      });
    }
    if (payload.contentAttachment) {
      const author = payloadAuthor(payload, byRef, provenanceOrganization);
      attachments.push({
        segmentId: payloadIdentifier(payload),
        payloadIndex,
        textSegmentNumber: textSegmentNumber > 0 ? textSegmentNumber : null,
        title: payload.contentAttachment.title ?? null,
        contentType: payload.contentAttachment.contentType ?? null,
        data: payload.contentAttachment.data ?? null,
        url: payload.contentAttachment.url ?? null,
        creation: stringValue(payload.contentAttachment.creation),
        authoredAt: author.at,
        authorName: author.name,
        authorPhone: author.phone,
      });
    }
  });
  const messageSender = messageSenderPerson(communication, byRef);
  const currentAuthor =
    (messageSender?.roleResource
      ? textAuthors.find((author) => author.roleResource?.id === messageSender.roleResource?.id)
      : undefined) ?? textAuthors[textAuthors.length - 1];
  const signature = signatureFrom(currentAuthor ?? emptyAuthor());

  const actCode =
    (provenance?.activity as { coding?: { code?: string }[] } | undefined)?.coding?.[0]?.code ??
    null;
  const messageTimestamp =
    (provenance?.occurredDateTime as string | undefined) ??
    (bundle.timestamp as string | undefined) ??
    null;

  const authorRole = messageSender?.roleResource ?? currentAuthor?.roleResource;
  const firstRole = resources.find((resource) => resource.resourceType === 'PractitionerRole');
  const senderOrgId =
    refId(messageHeader?.sender) ??
    refId(authorRole?.organization) ??
    refId(firstRole?.organization);
  const senderOrg = senderOrgId
    ? byRef.get(senderOrgId)
    : resources.find((resource) => resource.resourceType === 'Organization');

  const destinations = messageHeader?.destination as
    | { receiver?: { reference?: string } }[]
    | undefined;
  const receiverOrgId = destinations?.[0]?.receiver ? refId(destinations[0].receiver) : null;
  const receiverOrg = receiverOrgId ? byRef.get(receiverOrgId) : undefined;

  const authorPractitioner = messageSender?.practitioner ?? currentAuthor?.practitioner;
  const firstPractitioner = resources.find((resource) => resource.resourceType === 'Practitioner');
  const senderPractitionerName =
    messageSender?.name ?? personName(authorPractitioner) ?? personName(firstPractitioner);
  const senderRole = messageSender?.role ?? roleLabel(authorRole) ?? roleLabel(firstRole);
  const recipient = specificRecipient(communication, byRef);

  return {
    patientName,
    patientGiven,
    patientFamily,
    cpr: cprIdent?.value ?? null,
    xEcpr: xEcprIdent?.value ?? null,
    dEcpr: dEcprIdent?.value ?? null,
    categoryCode,
    topic,
    priority,
    activityCode: actCode,
    messageTimestamp,
    textSegments,
    attachments,
    senderOrganization: stringValue(senderOrg?.name),
    senderSor: sorValue(senderOrg),
    senderGln: glnValue(senderOrg),
    receiverOrganization: stringValue(receiverOrg?.name),
    receiverSor: sorValue(receiverOrg),
    receiverGln: glnValue(receiverOrg),
    senderPractitionerName,
    senderRole,
    senderPhone: telecomValue(
      [authorPractitioner, authorRole, firstPractitioner, firstRole],
      'phone',
    ),
    senderEmail: telecomValue(
      [authorPractitioner, authorRole, firstPractitioner, firstRole],
      'email',
    ),
    specificSenderName: specificSenderName(communication, byRef),
    specificSenderKind: specificSenderKind(communication, byRef),
    specificRecipientName: recipient.name,
    specificRecipientRole: recipient.role,
    specificRecipientKind: recipient.kind,
    signature,
    episodeOfCareId,
    episodeOfCareIdentifiers,
    encounterStatus: stringValue(encounter?.status),
    encounterClassCode: encounterClass.code,
    encounterClassSystem: encounterClass.system,
    otherIdentifiers: otherIdents.filter((identifier) => identifier.system && identifier.value) as {
      system: string;
      value: string;
    }[],
    organizations: organizationsFromResources(resources),
  };
}

function organizationsFromResources(resources: Resource[]): BundleOrganization[] {
  const organizations: BundleOrganization[] = [];
  for (const resource of resources) {
    if (resource.resourceType !== 'Organization') continue;
    const id = stringValue(resource.id);
    if (!id) continue;
    organizations.push({
      id,
      name: stringValue(resource.name),
      sor: sorValue(resource),
    });
  }
  return organizations;
}

function emptyAuthor(): PayloadAuthor {
  return {
    name: null,
    role: null,
    roleResource: undefined,
    practitioner: undefined,
    phone: null,
    at: null,
    organization: null,
  };
}
